import { motion } from 'framer-motion'
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import RealisticPlate from '@/components/shop/RealisticPlate'
import type { PlateSize, PlateType, VehicleCategory } from '@/services/rules/plateRulesEngine'

export interface AvailablePlate {
    city: string
    middle: string
    end: string
}

interface AvailabilityResultsProps {
    isLoading: boolean
    searchedPattern: string
    availablePlates: AvailablePlate[]
    error?: string | null
    onClear: () => void
    plateSize?: PlateSize
    plateType?: PlateType
    vehicleCategory?: VehicleCategory
    seasonFrom?: number
    seasonTo?: number
}

export default function AvailabilityResults({
    isLoading,
    searchedPattern,
    availablePlates,
    error,
    onClear,
    plateSize,
    plateType,
    vehicleCategory,
    seasonFrom,
    seasonTo,
}: AvailabilityResultsProps) {

    if (!isLoading && !searchedPattern && !error) return null;

    const hasResults = availablePlates.length > 0

    // Use a compact plate size for result display (always single-line 520x110)
    const resultPlateSize: PlateSize = plateSize || { width: 520, height: 110, layout: 'einzeilig' }
    const resultPlateType: PlateType = plateType || 'REGULAR'
    const resultVehicle: VehicleCategory = vehicleCategory || 'PKW'

    return (
        <div className="mt-8 bg-white/5 backdrop-blur-md border border-dark-700/50 rounded-2xl p-6 sm:p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">

            <div className="flex items-center justify-between border-b border-dark-700/50 pb-4 mb-6">
                <h3 className="text-xl font-bold text-white">
                    {hasResults ? `${availablePlates.length} verfügbare Kombination${availablePlates.length > 1 ? 'en' : ''}` : 'Suchergebnis'}
                </h3>
                <button onClick={onClear} className="text-sm text-gray-400 hover:text-white transition-colors">
                    ✕ Suche löschen
                </button>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-dark-700/50 border-t-primary-500 rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-400">Prüfe Verfügbarkeit bei der Zulassungsstelle...</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="flex items-center gap-3 text-red-400 text-lg font-semibold mb-2">
                        <FaTimesCircle className="w-6 h-6" />
                        Fehler bei der Suche
                    </div>
                    <p className="text-gray-400 text-sm text-center max-w-md">{error}</p>
                </div>
            ) : hasResults ? (
                <div className="grid gap-4">
                    {availablePlates.map((plate, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.06 }}
                            className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 bg-dark-900/40 border border-dark-700/50 rounded-xl hover:bg-dark-800/60 transition-colors"
                        >
                            {/* Realistic Plate Preview */}
                            <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="scale-[0.55] sm:scale-[0.65] origin-left -my-4">
                                    <RealisticPlate
                                        plateSize={resultPlateSize}
                                        plateType={resultPlateType}
                                        vehicleCategory={resultVehicle}
                                        input={{
                                            cityCode: plate.city,
                                            letters: plate.middle,
                                            numbers: plate.end,
                                        }}
                                        interactive={false}
                                        seasonFrom={seasonFrom}
                                        seasonTo={seasonTo}
                                    />
                                </div>
                            </div>

                            {/* Status + Action */}
                            <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="flex items-center gap-2 text-green-400">
                                    <FaCheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">Verfügbar</span>
                                </div>
                                <button className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-bold text-sm rounded-lg shadow-lg shadow-green-500/20 transition-all hover:scale-[1.03] active:scale-[0.97]">
                                    RESERVIEREN
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="flex items-center gap-3 text-amber-400 text-lg font-semibold mb-3">
                        <FaTimesCircle className="w-6 h-6" />
                        Keine verfügbaren Kennzeichen gefunden
                    </div>
                    <p className="text-gray-400 text-sm text-center max-w-md">
                        Für die Kombination <strong className="text-white">{searchedPattern}</strong> sind leider keine Kennzeichen verfügbar.
                        Versuchen Sie es mit einem <strong className="text-white">?</strong> als Platzhalter.
                    </p>
                </div>
            )}
        </div>
    )
}
